import { useCallback } from "react";
import {
  getInspectionResults,
  getInspectionResultsFullHierarchy,
} from "../../../api/InspectionResults";
import { getInspectionDefFullHierarchy } from "../../../api/InspectionDefinitions";
import { useWorkOrderStore } from "../../../store/wo-order";
import { useEquipmentStore } from "../../../store/equipment";

type InspectionContextOptions = {
  isEquipmentContext?: boolean;
  equipmentId?: string;
};

export default function useGetInspection() {
  const setWoInspectionData = useWorkOrderStore(
    (state) => state.setWoInspectionData
  );

  const setEquipInspectionData = useEquipmentStore(
    (state) => state.setEquipInspectionData
  );

  const getInspectionDefHierarchy = useCallback(
    async (
      inspectionId: number | string,
      woNumber: string,
      options?: InspectionContextOptions
    ) => {
      const defResponse = await getInspectionDefFullHierarchy({
        // explicitly convert to string -- iOS issue
        inspectionId: String(inspectionId),
        includeWoDetails: !options?.isEquipmentContext,
        includeEqDetails: !!options?.isEquipmentContext,
      });
      if (!options?.isEquipmentContext && woNumber) {
        setWoInspectionData(woNumber, {
          isCompleted: false,
          inspectionDef: defResponse.data,
          inspectionId: String(inspectionId),
        });
      } else if (options?.isEquipmentContext && options.equipmentId) {
        setEquipInspectionData(options.equipmentId, {
          isCompleted: false,
          inspectionDef: defResponse.data,
          inspectionId: String(inspectionId),
        });
      }
      return defResponse;
    },
    [setEquipInspectionData, setWoInspectionData]
  );

  const getInspection = useCallback(
    async (
      woNumber: string,
      inspectionId: string,
      options?: InspectionContextOptions
    ) => {
      let hasError = false;
      let data: any = undefined;
      let isCompleted = false;

      const isEquipmentContext =
        !!options?.isEquipmentContext && !!options?.equipmentId;

    
      if (isEquipmentContext) {
        const defResponse = await getInspectionDefHierarchy(
          inspectionId,
          woNumber,
          { isEquipmentContext: true, equipmentId: options?.equipmentId }
        );
        hasError = defResponse.isError;
        data = defResponse.data;
        const status = defResponse.status;
        return { hasError, data, status, isCompleted };
      }

      const buildFilter = (): string => {
        const safeInspectionId = String(inspectionId);
        if (isEquipmentContext) {
          const equipmentId = String(options?.equipmentId ?? "");
          return `equipment='${equipmentId}' AND inspection_id='${safeInspectionId}'`;
        }
        const [woBase, woTask, woSubtask] = woNumber.split("-");
        let filter = `wo_base='${woBase}' AND inspection_id='${safeInspectionId}'`;
        if (woTask) {
          filter += ` AND wo_task='${woTask}'`;
        }
        if (woSubtask) {
          filter += ` AND wo_subtask='${woSubtask}'`;
        }
        return filter;
      };

      let response = await getInspectionResults(buildFilter());
      hasError = response.isError;
      let status = response.status;
      data = response.data;
      if (!response.isError) {
        if (response.data?.length > 0) {
          const matchingResult = response.data.find((result: any) => {
            if (isEquipmentContext) {
              const apiEquipment = result.Equipment || "";
              return apiEquipment === String(options?.equipmentId ?? "");
            }

            let apiWorkOrder = result.WoBase || "";

            if (result.WoTask && result.WoTask.trim() !== "") {
              apiWorkOrder += `-${result.WoTask}`;
            }

            if (result.WoSubtask && result.WoSubtask.trim() !== "") {
              apiWorkOrder += `-${result.WoSubtask}`;
            }

            return apiWorkOrder === woNumber;
          });

          if (matchingResult) {
            const { ResultId, InspectionId } = matchingResult;
            const resultResponse = await getInspectionResultsFullHierarchy({
              //explicitly convert to string -- iOS issue
              inspectionId: String(InspectionId),
              resultId: String(ResultId),
            });
            hasError = resultResponse.isError;
            status = resultResponse.status;
            data = resultResponse.data;
            const inspectionResult = resultResponse.data?.InspectionResult;
            isCompleted = !!(
              inspectionResult?.ResultId && inspectionResult?.InspectedDatetime
            );
            // Cache only for work-order-based inspections
            if (!isEquipmentContext && woNumber) {
              setWoInspectionData(woNumber, {
                isCompleted,
                inspectionResults: resultResponse.data,
                inspectionId: InspectionId,
              });
            } else if (isEquipmentContext && options.equipmentId) {
              setEquipInspectionData(options.equipmentId, {
                isCompleted,
                inspectionResults: resultResponse.data,
                inspectionId: InspectionId,
              });
            }
          } else {
            // Results exist in the DB but none belong to this specific WO
            // (e.g. the base WO sees task-level results from the same inspection).
            // Each WO is independent — fall back to the definition so the inspection
            // renders as not-yet-completed with its points structure intact.
            const defResponse = await getInspectionDefHierarchy(
              inspectionId,
              woNumber,
              { isEquipmentContext, equipmentId: options?.equipmentId }
            );
            hasError = defResponse.isError;
            status = defResponse.status;
            data = defResponse.data;
          }
        } else {
          const defResponse = await getInspectionDefHierarchy(
            inspectionId,
            woNumber,
            { isEquipmentContext, equipmentId: options?.equipmentId }
          );

          hasError = defResponse.isError;
          status = defResponse.status;
          data = defResponse.data;
        }
      }
      return { hasError, data, status, isCompleted };
    },
    [getInspectionDefHierarchy, setEquipInspectionData, setWoInspectionData]
  );

  return { getInspection, getInspectionDefHierarchy };
}
